import { Injectable, Signal, computed, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, map, of, tap } from 'rxjs';
import { Transaction, MiniTransaction, KeyedMiniTransaction, GroupKeyedMiniTransaction } from '../interfaces/transaction';
import { Race } from '../interfaces/race';
import { transition } from '@angular/animations';
import { toSignal } from '@angular/core/rxjs-interop';
import { Government } from '../interfaces/government';

@Injectable({
  providedIn: 'root'
})
export class TransactionService {
  constructionHash: { [key: string]: any; } = {};

  constructor (private http: HttpClient) { }

  // Populations Units   Populations         Conversion Factor
  // 1-16 PU             1-16 PTU
  // 17-50 PU            17-50 PTU
  // 51-150 PU           51-150 PTU
  // 151-400 PU          153-800 PTU         PU = 2.6 PTU
  // 401-800 PU          818-8,000 PTU       PU = 18 PTU
  // 801-1,600 PU        8,090-80,000 PTU    PU = 90 PTU
  // 1,601-3,200 PU      80,450-800,000 PTU  PU = 450 PTU
  ptuToPu (ptu: number): number {
    let pu = 0;
    if (ptu >= 800000) {
      pu = 3200;
    }
    else if (ptu > 80000) {
      pu = 1600 + Math.floor((ptu - 80000) / 450);
    }
    else if (ptu > 8000) {
      pu = 800 + Math.floor((ptu - 8000) / 90);
    }
    else if (ptu > 800) {
      pu = 400 + Math.floor((ptu - 800) / 18);
    }
    else if (ptu > 150) {
      pu = 150 + Math.floor((ptu - 150) / 2.6);
    }
    else {
      pu = ptu;
    }
    return pu;
  };

  puToPtu (pu: number): number {
    var ptu = 0;
    if (pu >= 3200) {
      ptu = 800000;
    }
    else if (pu > 1600) {
      ptu = 80000 + (pu - 1600) * 450;
    }
    else if (pu > 800) {
      ptu = 8000 + (pu - 800) * 90;
    }
    else if (pu > 400) {
      ptu = 800 + (pu - 400) * 18;
    }
    else if (pu > 150) {
      ptu = 150 + Math.round((pu - 150) * 2.6);
    }
    else {
      ptu = pu;
    }
    return ptu;
  };

  buildBaseSummary (): KeyedMiniTransaction {
    let keys = ['popIncome', 'Warships', 'Defenses', 'Freighters', 'Population', 'industrial', 'tradeIncome', 'leaseIncome', 'shipSale', 'otherIncome', 'maintenance', 'construction', 'colonization', 'troops', 'research', 'techItem', 'OtherExpense', 'Grand'];
    let baseSummary: KeyedMiniTransaction = {};
    for (const key of keys) {
      baseSummary[key + 'Total'] = { amount: 0 };
    }
    baseSummary['GrandTotal']['remainder'] = 0;
    return baseSummary;
  };

  buildSummaryFromTransactions (transactions: Transaction[] = [], government: Government | undefined = undefined): KeyedMiniTransaction {
    let summary: KeyedMiniTransaction = {};
    if (transactions.length >= 0) {
      summary = this.buildBaseSummary();
      for (const transaction of transactions) {
        let key = transaction.type;
        if (transaction.locator === "FreeSY") {
          key = "Defenses";
        }
        let keyTotal = key + 'Total';
        let totalTransaction: MiniTransaction = summary[keyTotal] || { amount: 0 };
        totalTransaction.amount = totalTransaction.amount + transaction.amount;
        summary[keyTotal] = totalTransaction;
        if (key != 'popIncome') {
          summary['GrandTotal'].amount = summary['GrandTotal'].amount + transaction.amount;
        }
        else {
          //locator
          let locatorElements = transaction.locator?.split("-") || [];
          let starSystemNumber = locatorElements[0] || 'none';
          let newKey = 'popIncome' + starSystemNumber;
          let starSystemSubtotal = summary[newKey] || { amount: 0 };
          starSystemSubtotal.amount = starSystemSubtotal.amount + transaction.amount;
          summary[newKey] = starSystemSubtotal;
        }
      }
      if (government) {
        summary['industrialTotal'].amount = (summary['popIncomeTotal'].amount || 0) * (government!.industrial || 0) / 100;
        summary['GrandTotal'].amount = summary['GrandTotal'].amount + summary['industrialTotal'].amount;
      }
      summary['GrandTotal'].remainder = summary['popIncomeTotal'].amount - summary['GrandTotal'].amount;
    }
    return summary;
  };

  private clearTransactionData (raceId: string, turn: number) {
    localStorage.removeItem('transactions' + raceId + turn);
    this.constructionHash = {};
  };

  private getSavedTransactionData (raceId: string, turn: number): Observable<Transaction[]> {
    return this.http.get<Transaction[]>('/api/economy/raceid/' + raceId + '/turn/' + turn);
  };

  private getIncomeData (raceId: string, turn: number): Observable<Transaction[]> {
    return this.http.put<Transaction[]>('/api/economy/income/raceid/' + raceId + '/turn/' + turn, null, { observe: "body", responseType: "json" });
  };

  private buildTransactionData (raceId: string, turn: number): Observable<Transaction[]> {
    let transactions$ = this.getSavedTransactionData(raceId, turn);
    // income can be impacted by prior turn changes; needs to be refetched
    let income$ = this.getIncomeData(raceId, turn);
    return forkJoin([transactions$, income$]).pipe(
      map(allTransactions => {
        // this reduction is to remove duplicate transactions by id, new income transactions override old
        let transactionsBaseHash: { [key: string]: Transaction; } = {};
        let transactionsHash: { [key: string]: Transaction; } = [...allTransactions[0], ...allTransactions[1]].reduce
          ((transactionHash: { [key: string]: Transaction; }, transaction: Transaction) => {
            transactionHash[transaction._id] = transaction;
            return transactionHash;
          }, transactionsBaseHash);
        return Object.values(transactionsHash);
      })
    );
  };

  getOrBuildTransactions (race: Race, turn: number): Observable<Transaction[]> {
    let transactions$;
    if (turn <= (race.economicsCompleteTurn || 0)) {
      // transactions are cached only if turn is in the past
      let cachedTransactionsJson = localStorage.getItem('transactions' + race._id + turn);
      let cachedTransactions;
      if (cachedTransactionsJson) {
        cachedTransactions = JSON.parse(cachedTransactionsJson);
        transactions$ = of(cachedTransactions);
      }
      else {
        transactions$ = this.getSavedTransactionData(race._id, turn).pipe(
          tap(combinedTransactions => {
            localStorage.setItem('transactions' + race._id + turn, JSON.stringify(combinedTransactions));
          })
        );
      }
    }
    else {
      transactions$ = this.buildTransactionData(race._id, turn);
    }
    return transactions$;
  };

  performStartup (raceId: string, turn: number) {
    this.clearTransactionData(raceId, turn);
    return this.http.post<Race>('/api/economy/performStartup/race/' + raceId + '/turn/' + turn, null, { observe: "body", responseType: "json" });
  };

  rollbackStartup (raceId: string, turn: number) {
    this.clearTransactionData(raceId, turn);
    return this.http.post<Race>('/api/economy/rollbackStartup/race/' + raceId + '/turn/' + turn, null, { observe: "body", responseType: "json" });
  };

  saveTransaction (transaction: Transaction): Observable<Transaction> {
    let save: Observable<Transaction>;
    if (!transaction._id) {
      save = this.http.post<Transaction>('/api/economy', transaction, { observe: "body", responseType: "json" });
    } else {
      save = this.http.put<Transaction>('/api/economy/' + transaction._id, transaction, { observe: "body", responseType: "json" });
    }
    return save;
  };

  deleteTransactionById (id: string) {
    return this.http.delete('/api/economy/' + id);
  };

  deleteTransaction (transaction: Transaction) {
    return this.deleteTransactionById(transaction._id);
  };

  getConstructionData$ (raceId) {
    return this.http.get<Transaction[]>('/api/economy/construction/raceid/' + raceId);
  };

  processConstructionTransactions (data) {
    if (Array.isArray(data) && data.length > 0) {
      this.constructionHash = data.reduce(function (constructionHash, transaction) {
        constructionHash[transaction.turn + '-' + transaction.locator + '-' + transaction.sequence] = transaction;
        return constructionHash;
      }, this.constructionHash);
    }
    return this.constructionHash;
  };
};

// this.getSpecificTransactionData = function (raceId, turn, type) {
//     let transactions = getCached_Transactions(raceId, turn);
//     if (transactions && transactions != {}) {
//         return $q.when(transactions);
//     }
//     return this.http.get<Transaction[]>('/api/economy/raceid/'+raceId+'/turn/'+turn+'/type/'+type).then( process_Transactions ).then(
//         function (result) {
//             // this data should NOT be cached
//             this.turnData[turn] = null;
//             return result;
//         }
//     );
// };

// this.findNextEconomicLevelResearchForRace = function (raceId) {
//     return this.http.put('/api/economy/research/' + raceId, null, { observe: "body", responseType: "json" }).then(
//         function (result) {
//             return result.data;
//         }
//     );
// };
